import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  alpha,
  IconButton,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { useUpdateTenantMutation } from "../../../features/tenants/tenantsApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";

export default function ModuleAddDialog({ tenant, module }) {
  const [open, setOpen] = React.useState(false);
  const [updateTenant] = useUpdateTenantMutation();
  const dispatch = useDispatch();
  const isUnlimited = React.useMemo(() => {
    if (!tenant) return false;
    if (module === "USER") return !Number.isFinite(tenant.allowedUsers);
    if (module === "MEMBER") return !Number.isFinite(tenant.allowedMembers);
    if (module === "SITE") return !Number.isFinite(tenant.allowedSites);
    return false;
  }, [tenant, module]);

  const handleClickOpen = () => {
    if (isUnlimited) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleBookModule = async () => {
    let updateData;

    if (module === "USER") {
      updateData = { allowedUsers: tenant.allowedUsers + 10 };
    }

    if (module === "MEMBER") {
      updateData = { allowedMembers: tenant.allowedMembers + 50 };
    }

    if (module === "SITE") {
      updateData = { allowedSites: tenant.allowedSites + 1 };
    }

    try {
      let result = await updateTenant(updateData).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Erfolgreich gebucht",
            type: "SUCCESS",
            data: {
              successText: result?.message ?? "Ihr Modul wurde erfolgreich erweitert.",
            },
          })
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText: "Fehler beim Buchen. Bitte versuchen Sie es erneut.",
          },
        })
      );
    } finally {
      handleClose();
    }
  };

  const renderMemberPackage = () => {
    return (
      <Card
        sx={{
          border: "2px solid",
          borderColor: "success.main",
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.05),
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                }}
              >
                <Iconify
                  icon="eva:people-fill"
                  width={28}
                  sx={{ color: "success.main" }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Mitglieder-Erweiterung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Erweitern Sie Ihre Mitgliederkapazität
                </Typography>
              </Box>
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight={700} color="success.main">
                5€
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  / Monat
                </Typography>
              </Typography>
              <Chip label="+50 Mitglieder" color="success" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderUserPackage = () => {
    return (
      <Card
        sx={{
          border: "2px solid",
          borderColor: "primary.main",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Iconify
                  icon="eva:person-add-fill"
                  width={28}
                  sx={{ color: "primary.main" }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Benutzer-Erweiterung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fügen Sie weitere System-Benutzer hinzu
                </Typography>
              </Box>
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight={700} color="primary.main">
                10€
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  / Monat
                </Typography>
              </Typography>
              <Chip label="+10 Benutzer" color="primary" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderSitePackage = () => {
    return (
      <Card
        sx={{
          border: "2px solid",
          borderColor: "secondary.main",
          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.05),
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                }}
              >
                <Iconify
                  icon="eva:briefcase-fill"
                  width={28}
                  sx={{ color: "secondary.main" }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Standort-Erweiterung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fügen Sie einen weiteren Standort hinzu
                </Typography>
              </Box>
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight={700} color="secondary.main">
                10€
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  / Monat
                </Typography>
              </Typography>
              <Chip label="+1 Standort" color="secondary" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const getModuleConfig = () => {
    switch (module) {
      case "USER":
        return {
          title: "Benutzer hinzufügen",
          icon: "eva:person-add-fill",
          color: "primary",
          buttonText: "Benutzer hinzufügen",
        };
      case "MEMBER":
        return {
          title: "Mitglieder hinzufügen",
          icon: "eva:people-fill",
          color: "success",
          buttonText: "Mitglieder hinzufügen",
        };
      case "SITE":
        return {
          title: "Standort hinzufügen",
          icon: "eva:briefcase-fill",
          color: "secondary",
          buttonText: "Standort hinzufügen",
        };
      default:
        return {
          title: "Modul hinzufügen",
          icon: "eva:plus-fill",
          color: "primary",
          buttonText: "Hinzufügen",
        };
    }
  };

  const config = getModuleConfig();

  const getButtonText = () => {
    if (isUnlimited) {
      return "Bereits unbegrenzt";
    }
    switch (module) {
      case "MEMBER":
        return "Für 5€/Monat buchen";
      default:
        return "Für 10€/Monat buchen";
    }
  };

  return (
    <React.Fragment>
      <Button
        variant="contained"
        color={config.color}
        fullWidth
        startIcon={<Iconify icon={config.icon} />}
        onClick={handleClickOpen}
        disabled={isUnlimited}
        sx={{
          py: 1.5,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "1rem",
        }}
      >
        {isUnlimited ? "Unbegrenzt enthalten" : config.buttonText}
      </Button>

      {!isUnlimited && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette[config.color].main, 0.1),
                }}
              >
                <Iconify
                  icon={config.icon}
                  width={24}
                  sx={{ color: `${config.color}.main` }}
                />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {config.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Erweitern Sie Ihre Kapazität
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Iconify icon="eva:close-outline" width={24} />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3, pb: 2 }}>
            {module === "USER" && renderUserPackage()}
            {module === "MEMBER" && renderMemberPackage()}
            {module === "SITE" && renderSitePackage()}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleBookModule}
              variant="contained"
              color={config.color}
              sx={{ minWidth: 180, fontWeight: 600 }}
              startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
              {getButtonText()}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}
